// apps/client/src/pages/SelectOrganisation.tsx

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, Stack, Container, Fade, Button, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import OrganisationCard from '../../components/OrganisationCard';
import { createOrganisation, switchOrganisation } from '../../services';

const SelectOrganisation: React.FC = () => {
  const navigate = useNavigate();
  const { organisations, currentOrganisation, refreshUserOrganisationState } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasAutoSelected = useRef(false); // Prevent multiple auto-selections

  const handleSelectOrganisation = useCallback(
    async (organisationId: string) => {
      try {
        setError(null);
        setIsLoading(true);

        await switchOrganisation(organisationId);
        await refreshUserOrganisationState();
        navigate('/');
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to switch organisation. Please try again.';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, refreshUserOrganisationState]
  );

  const handleCreateOrganisation = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);

      const { organisation } = await createOrganisation();
      await switchOrganisation(organisation.id);
      await refreshUserOrganisationState();

      await navigate('/onboarding/organisation');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to create organisation. Please check your connection and try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, refreshUserOrganisationState]);

  // Initial data fetch - only run once
  useEffect(() => {
    refreshUserOrganisationState();
  }, []); // Empty dependency array - only run once

  // Auto-select if only one organisation and no current selection
  useEffect(() => {
    if (
      organisations &&
      organisations.length === 1 &&
      !currentOrganisation &&
      !hasAutoSelected.current &&
      !isLoading
    ) {
      hasAutoSelected.current = true;
      handleSelectOrganisation(organisations[0].id);
    }
  }, [organisations, currentOrganisation, handleSelectOrganisation, isLoading]);

  // Loading state
  if (!organisations) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography>Loading organisations...</Typography>
      </Box>
    );
  }

  // Sort organisations: by role (OWNER > ADMIN > MEMBER > VIEWER)
  const sortedOrganisations = [...organisations].sort((a, b) => {
    const roleOrder = { OWNER: 0, ADMIN: 1, MEMBER: 2, VIEWER: 3 };
    const aOrder = roleOrder[a.role as keyof typeof roleOrder] ?? 4;
    const bOrder = roleOrder[b.role as keyof typeof roleOrder] ?? 4;
    return aOrder - bOrder;
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={500}>
          <Box>
            {organisations.length > 0 ? (
              <Box textAlign="center" mb={4}>
                <Typography variant="h4" gutterBottom fontWeight={600}>
                  Select Organisation
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Choose which organisation you want to work with
                </Typography>
              </Box>
            ) : (
              <Box textAlign="center" mb={4}>
                <Typography variant="h4" gutterBottom fontWeight={600}>
                  Select Organisation
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  You currently do not belong to any organisation, you can create a new one here or
                  wait until you receive an invitation to join an existing organisation.
                </Typography>
              </Box>
            )}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Current Organisation Alert */}
            {currentOrganisation && (
              <Alert severity="info" sx={{ mb: 3 }} icon={<CheckCircleIcon />} variant="filled">
                Currently working in: <strong>{currentOrganisation.name}</strong>
              </Alert>
            )}

            {/* Organisation List */}
            <Stack spacing={2}>
              {sortedOrganisations.map((org) => {
                return (
                  <OrganisationCard
                    key={org.id}
                    organisation={{
                      id: org.id,
                      name: org.name || 'Unnamed Organisation',
                      legalName: org.name,
                      role: org.role,
                      joinedAt: org.joinedAt ? org.joinedAt.toString() : new Date().toISOString(),
                    }}
                    isSelected={currentOrganisation?.id === org.id}
                    onSelect={handleSelectOrganisation}
                  />
                );
              })}
            </Stack>

            {/* Bottom Actions */}
            <Stack spacing={2} mt={4}>
              <Button
                variant="outlined"
                startIcon={<AddBusinessIcon />}
                onClick={handleCreateOrganisation}
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create New Organisation'}
              </Button>

              <Typography variant="body2" color="text.secondary" textAlign="center">
                You can switch organisations at any time from your profile settings
              </Typography>
            </Stack>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default SelectOrganisation;
